import React, { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import PropTypes from 'prop-types'
import { ImageNotSupported as ImageNotSupportedIcon } from '@mui/icons-material'
import { Button, Grid, Typography, Skeleton, Stack } from '@mui/material'
import { CameraAlt, CloudDownload } from '@mui/icons-material_modern'
import { useTheme } from '@mui/material/styles'
import { useGetOrgImageQuery } from 'services/assets'
import ImageAnalyzer from './ImageAnalyzer'
import { dispatchSnackbar } from 'utils/helpers'

const imageStyles = {
  width: '100%',
  aspectRatio: '2/1',
  borderRadius: '4px',
  transform: 'unset',
  cursor: 'pointer',
}

const MaterialAndTruckImage = ({ imageFilename, deviceName, isLoading, onImageClick }) => {
  const theme = useTheme()
  const dispatch = useDispatch()

  const { canUseMetalDetector } = useSelector(state => state.featureFlags?.variables || {})

  const { data, isFetching, error } = useGetOrgImageQuery({ filename: imageFilename }, { skip: !imageFilename })

  const handleDownload = () => {
    const link = document.createElement('a')
    link.href = data
    link.download = true
    link.target = '_blank'
    link.rel = 'noopener noreferrer'
    link.click()
  }

  useEffect(() => {
    if (error?.data?.code === 'NOT_FOUND') {
      dispatchSnackbar(
        dispatch,
        'error',
        'Image was not captured successfully by the camera. Please delete and try capturing again.',
      )
    }
  }, [error])

  const renderImage = () => {
    if (isLoading || isFetching) {
      return <Skeleton animation="wave" sx={imageStyles} />
    }
    if (error) {
      return (
        <Stack
          alignItems="center"
          justifyContent="center"
          height="100%"
          style={{ ...imageStyles, color: theme.palette.text.hint }}
        >
          <ImageNotSupportedIcon />
          <Typography fontSize="small">Error capturing image.</Typography>
          <Typography fontSize="small">Please try again.</Typography>
        </Stack>
      )
    }
    return imageFilename ? (
      <div style={{ position: 'relative' }}>
        <img src={data} alt="" style={imageStyles} onClick={onImageClick} />
        <Button
          variant="contained"
          size="small"
          color="primary"
          onClick={handleDownload}
          style={{ position: 'absolute', top: '8px', right: '8px' }}
        >
          <CloudDownload />
        </Button>
        {canUseMetalDetector && (
          <div style={{ position: 'absolute', top: '8px', right: '80px' }}>
            <ImageAnalyzer imageUrl={data} />
          </div>
        )}
      </div>
    ) : (
      <CameraAlt
        style={{
          ...imageStyles,
          color: theme.palette.text.hint,
          border: 1,
          borderStyle: 'solid',
          borderColor: theme.palette.text.disabled,
          height: 'unset',
        }}
      />
    )
  }

  return (
    <Grid item xs={6} pr={1}>
      <Typography variant="caption" color="text.secondary">
        {deviceName}
      </Typography>
      {renderImage()}
    </Grid>
  )
}

MaterialAndTruckImage.propTypes = {
  imageFilename: PropTypes.string.isRequired,
  deviceName: PropTypes.string,
  isLoading: PropTypes.bool,
  onImageClick: PropTypes.func,
}

MaterialAndTruckImage.defaultProps = {
  deviceName: 'Camera Unknown',
  isLoading: false,
  onImageClick: () => {},
}

export default MaterialAndTruckImage
